<template>
  <teleport to="body" v-if="showRotateDeviceOverlay">
    <div class="rotate-device-overlay" role="alert" aria-live="polite">
      <div class="rotate-device-overlay__content">
        <div class="rotate-device-overlay__content-inner">
          <img
            :src="RotatePhoneIcon"
            alt="Иконка поворота устройства"
            class="rotate-device-overlay__icon"
          />
          <p class="rotate-device-overlay__text">
            Для работы с сервисом, пожалуйста, поверните экран горизонтально
          </p>
        </div>
      </div>
    </div>
  </teleport>
  <a-config-provider
    :locale="ruLocale"
    :theme="{
      algorithm: currentThemeAlgorithm,
      token: BASE_THEME_SEED,
    }"
  >
    <router-view />
    <component :is="contextHolder" />
    <a-tour
      v-if="tourStore.currentTourSteps"
      :steps="tourStore.currentTourSteps"
      v-model:current="tourStore.currentTourState.currentStep"
      :open="tourStore.currentTourState.isOpened"
      @close="tourStore.currentTourState.isOpened = false"
    />
  </a-config-provider>
</template>

<script setup lang="ts">
import { useModalsStore } from "@/stores/modals/modalsStore";
import { useThemeStore } from "@/stores/theme.store";
import { computed, onMounted, onUnmounted, ref, watch } from "vue";
import { applyAntdTokensToCssVars } from "@/theme/applyAntdTokensToCssVars";
import { BASE_THEME_SEED } from "@/theme/tokens";
import ru_RU from "ant-design-vue/es/locale/ru_RU";
import relativeTime from "dayjs/plugin/relativeTime";
import dayjs from "dayjs";
import router from "@/router";
import { useHead } from "@unhead/vue";
import { usePartner } from "@/stores/partner/partner";
import { useUser } from "@/stores/user/user";
import { useOnboardingTours } from "@/stores/onboardingToures/onboardingTours";
import RotatePhoneIcon from "@/assets/images/rotate-phone.svg";

dayjs.extend(relativeTime);

const userStore = useUser();
const partnerStore = usePartner();
const updatePartnerDataTimeout = ref<number | null>(null);
const themeStore = useThemeStore();
const modalsStore = useModalsStore();
const { contextHolder } = modalsStore;
const tourStore = useOnboardingTours();
const DEFAULT_PAGE_TITLE = "Baskit. Единый личный кабинет для маркетплейсов";

const ruLocale = ref(ru_RU);
const isPortrait = ref(false);
const isMobileViewport = ref(false);
const isTouchDevice = ref(false);
const mediaQueries = ref<MediaQueryList[]>([]);
const isRotateOverlayDismissed = ref(false);

const currentThemeAlgorithm = computed(() => themeStore.currentThemeAlgorithm);
const isRotateOverlayDisabledOnRoute = computed(() =>
  Boolean(router.currentRoute.value.meta?.hideRotateDeviceOverlay),
);
const showRotateDeviceOverlay = computed(
  () =>
    userStore.userIsLoggined &&
    !isRotateOverlayDisabledOnRoute.value &&
    !isRotateOverlayDismissed.value &&
    isPortrait.value &&
    isMobileViewport.value &&
    isTouchDevice.value,
);

applyAntdTokensToCssVars(BASE_THEME_SEED, currentThemeAlgorithm.value);

watch(
  () => themeStore.isDarkTheme,
  () => {
    applyAntdTokensToCssVars(BASE_THEME_SEED, currentThemeAlgorithm.value);
  },
);

useHead({
  title: DEFAULT_PAGE_TITLE,
});

const updatePartnerData = async () => {
  await partnerStore.getPartner();
};

watch(
  () => userStore.user?.partnerId,
  async (partnerId) => {
    if (partnerId) {
      if (updatePartnerDataTimeout.value) {
        window.clearInterval(updatePartnerDataTimeout.value);
      }
      await updatePartnerData();
      updatePartnerDataTimeout.value = window.setInterval(
        updatePartnerData,
        1000 * 60 * 5,
      );
    } else if (updatePartnerDataTimeout.value) {
      window.clearInterval(updatePartnerDataTimeout.value);
      updatePartnerDataTimeout.value = null;
    }
  },
  { immediate: true },
);

watch(
  () => userStore.user?.id,
  async () => {
    await tourStore.loadUserTourState(true);
    setTimeout(() => {
      tourStore.openTour();
    });
  },
  { immediate: true, deep: true },
);

watch(
  () => router.currentRoute.value?.meta?.tourName,
  (newValue) => {
    if (newValue) {
      tourStore.openTour();
    }
  },
  { immediate: true, deep: true },
);

watch(
  () => userStore.userIsLoggined,
  (isLoggedIn) => {
    if (!isLoggedIn) {
      isRotateOverlayDismissed.value = false;
    }
  },
);

watch(
  showRotateDeviceOverlay,
  (isVisible) => {
    document.documentElement.classList.toggle(
      "rotate-device-overlay-open",
      isVisible,
    );
    document.body.classList.toggle("rotate-device-overlay-open", isVisible);
  },
  { immediate: true },
);

const updateDeviceOrientationState = () => {
  isPortrait.value = window.matchMedia("(orientation: portrait)").matches;
  isMobileViewport.value = window.matchMedia("(max-width: 1024px)").matches;
  isTouchDevice.value =
    window.matchMedia("(pointer: coarse)").matches ||
    navigator.maxTouchPoints > 0;
};

const dismissRotateDeviceOverlay = () => {
  isRotateOverlayDismissed.value = true;
};

onMounted(() => {
  const queries = [
    window.matchMedia("(orientation: portrait)"),
    window.matchMedia("(max-width: 1024px)"),
    window.matchMedia("(pointer: coarse)"),
  ];
  mediaQueries.value = queries;

  updateDeviceOrientationState();
  window.addEventListener("resize", updateDeviceOrientationState);
  queries.forEach((query) => {
    query.addEventListener("change", updateDeviceOrientationState);
  });
});

onUnmounted(() => {
  if (updatePartnerDataTimeout.value) {
    window.clearInterval(updatePartnerDataTimeout.value);
    updatePartnerDataTimeout.value = null;
  }

  window.removeEventListener("resize", updateDeviceOrientationState);
  mediaQueries.value.forEach((query) => {
    query.removeEventListener("change", updateDeviceOrientationState);
  });
  mediaQueries.value = [];

  document.documentElement.classList.remove("rotate-device-overlay-open");
  document.body.classList.remove("rotate-device-overlay-open");
});
</script>

<style lang="scss" scoped>
.rotate-device-overlay {
  position: fixed;
  inset: 0;
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  overscroll-behavior: contain;
  touch-action: none;
  padding: 24px;
  background: rgba(12, 18, 30, 0.9);
}

.rotate-device-overlay__content {
  position: relative;
  width: min(320px, calc(100vw - 48px));
  z-index: 10000;
}

.rotate-device-overlay__content-inner {
  position: relative;
  text-align: center;
  color: #ffffff;
}

.rotate-device-overlay__close {
  position: absolute;
  top: -8px;
  right: -8px;
  border: none;
  border-radius: 20px;
  padding: 6px 12px;
  color: #0f172a;
  background: #ffffff;
  font-size: 13px;
  line-height: 1;
  cursor: pointer;
}

.rotate-device-overlay__icon {
  width: 92px;
  height: 92px;
  margin-bottom: 18px;
}

.rotate-device-overlay__text {
  margin: 0;
  font-size: 18px;
  line-height: 1.4;
  font-weight: 500;
}

:global(html.rotate-device-overlay-open),
:global(body.rotate-device-overlay-open) {
  overflow: hidden !important;
}
</style>
